import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Play } from "lucide-react";
import Logo from "./Logo";
import SocialLinks from "./SocialLinks";
import LanguageSwitcher from "./LanguageSwitcher";
import { AudioToggle } from "./AudioProvider";
import { useVideo } from "./VideoModal";
import { SITE } from "@/data/site";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { openVideo } = useVideo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const link = "font-lab cursor-pointer text-[11px] uppercase tracking-[0.18em] text-[#56636b] transition-colors hover:text-[#0a0a0a]";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "border-[#e4e9ec] bg-white/85 backdrop-blur-xl" : "border-transparent bg-white/40 backdrop-blur-md"
      }`}
      data-testid="site-nav"
    >
      <div className="absolute inset-x-0 top-0 h-16 bg-white" aria-hidden="true" />
      <div className="relative mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-6 px-5 md:px-10">
        <button onClick={() => navigate("/")} data-testid="nav-logo-button" className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          <button className={link} onClick={() => go("#collection")} data-testid="nav-collection">Collection</button>
          <button className={link} onClick={() => go("#science")} data-testid="nav-science">Science</button>
          <button className={link} onClick={() => navigate("/team")} data-testid="nav-team-link">Meet the Team</button>
          <button className={`${link} hidden xl:inline-block`} onClick={() => navigate("/certification")} data-testid="nav-certification">Compliance</button>
          <button className={`${link} hidden xl:inline-block`} onClick={() => navigate("/education")} data-testid="nav-education">Research Education</button>
          <button className={`${link} hidden xl:inline-block`} onClick={() => navigate("/whitepaper")} data-testid="nav-whitepaper">White Paper</button>
          <button className={link} onClick={() => go("#partner")} data-testid="nav-partner">Partner</button>
          <button className={`${link} hidden xl:inline-block`} onClick={() => navigate("/careers")} data-testid="nav-careers">Join Our Team</button>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <AudioToggle />
          <button
            onClick={() => openVideo()}
            data-testid="watch-film-button"
            className="inline-flex items-center gap-2 border border-[#e4e9ec] px-3.5 py-2 text-xs font-medium text-[#0a0a0a] transition-colors hover:border-[#0a0a0a]"
          >
            <Play className="h-3.5 w-3.5" /> Watch the film
          </button>
          <LanguageSwitcher />
          <SocialLinks />
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <AudioToggle testId="audio-toggle-mobile" />
          <button
            className="inline-flex h-10 w-10 items-center justify-center border border-[#e4e9ec]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-t border-[#e4e9ec] bg-white px-5 pb-8 pt-6 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-5">
              <button className="text-left font-display text-2xl" onClick={() => go("#collection")} data-testid="nav-mobile-collection">Collection</button>
              <button className="text-left font-display text-2xl" onClick={() => go("#science")} data-testid="nav-mobile-science">Science</button>
              <button className="text-left font-display text-2xl" onClick={() => { setOpen(false); navigate("/team"); }} data-testid="nav-mobile-team">Meet the Team</button>
              <button className="text-left font-display text-2xl" onClick={() => { setOpen(false); navigate("/certification"); }} data-testid="nav-mobile-certification">Certification & Compliance</button>
              <button className="text-left font-display text-2xl" onClick={() => { setOpen(false); navigate("/education"); }} data-testid="nav-mobile-education">Research Education</button>
              <button className="text-left font-display text-2xl" onClick={() => { setOpen(false); navigate("/whitepaper"); }} data-testid="nav-mobile-whitepaper">White Paper</button>
              <button className="text-left font-display text-2xl" onClick={() => go("#partner")} data-testid="nav-mobile-partner">Partner</button>
              <button className="text-left font-display text-2xl" onClick={() => { setOpen(false); navigate("/careers"); }} data-testid="nav-mobile-careers">Join Our Team</button>
              <button className="inline-flex items-center gap-2 text-left text-sm font-medium" onClick={() => { setOpen(false); openVideo(); }} data-testid="nav-mobile-film">
                <Play className="h-4 w-4" /> Watch the film
              </button>
              <div className="flex items-center justify-between pt-2">
                <LanguageSwitcher />
                <SocialLinks />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
